import {ofType} from 'redux-observable';
import {of, concat} from 'rxjs';
import {flatMap, catchError} from 'rxjs/operators';
import {getFormValues, isDirty, change} from 'redux-form';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {showDialogObservable} from 'web-app/react/dialog';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Api from './api';
import * as Actions from './actions';
import * as Constants from './constants';
import {type FormData, fieldName} from './types';

const periodUpdated = (action$, state$) =>
    action$.pipe(
        ofType(Actions.periodUpdated.type),
        flatMap(({payload}: ReturnType<typeof Actions.periodUpdated.action>) => {
            const formValues = getFormValues(Constants.FormName)(state$.value) as FormData | undefined;

            if (!formValues) {
                return of(Actions.periodUpdatedAborted.action());
            }

            const {from, to} = formValues;

            if (!from || !to) {
                return of(Actions.periodUpdatedAborted.action());
            }

            const pushedFrom = payload.fieldName === 'to' && to < from ? to : undefined;
            const pushedTo = payload.fieldName === 'from' && from > to ? from : undefined;

            const changePeriodActions = [
                pushedFrom ? of(change(Constants.FormName, fieldName('from'), pushedFrom)) : undefined,
                pushedTo ? of(change(Constants.FormName, fieldName('to'), pushedTo)) : undefined,
            ].filter(hasValue);

            return concat(
                ...changePeriodActions,
                Api.daysForPeriod(payload.institutionSetId, pushedFrom || from, pushedTo || to).pipe(
                    flatMap(response => {
                        return of(Actions.periodUpdatedSuccess.action(response));
                    }),
                    catchError(error => {
                        return of(Actions.periodUpdatedFailed.action(error));
                    }),
                ),
            );
        }),
    );

const fetchRecipients = action$ =>
    action$.pipe(
        ofType(Actions.fetchRecipients.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchRecipients.action>) => {
            return Api.getPossibleRecipients(payload.institutionSetId).pipe(
                flatMap(response => {
                    return of(Actions.fetchRecipientsSuccess.action(response));
                }),
                catchError(error => {
                    return of(Actions.fetchRecipientsFailed.action(error));
                }),
            );
        }),
    );

const submit = action$ =>
    action$.pipe(
        ofType(Actions.submit.type),
        flatMap(({payload}: ReturnType<typeof Actions.submit.action>) => {
            return showDialogObservable({
                title: i18next.t('calendar.vacationPeriod.submitDialogTitle'),
                description: i18next.t('calendar.vacationPeriod.submitDialogDescription'),
                okText: i18next.t('send'),
            }).pipe(
                flatMap(selection => {
                    if (selection === 'OK') {
                        return Api.create(payload.data).pipe(
                            flatMap(response => {
                                return [
                                    GlobalEventActions.updateSuccess.action(
                                        i18next.t('calendar.vacationPeriod.created', {title: payload.data.title}),
                                    ),
                                    Actions.submitSuccess.action(response),
                                    Actions.hide.action(),
                                ];
                            }),
                            catchError(error => {
                                return of(Actions.submitFailed.action(error));
                            }),
                        );
                    } else {
                        return of(Actions.submitAborted.action());
                    }
                }),
            );
        }),
    );

const close = (action$, state$) =>
    action$.pipe(
        ofType(Actions.close.type),
        flatMap(() => {
            const isFormDirty = isDirty(Constants.FormName)(state$.value);

            if (!isFormDirty) {
                return of(Actions.hide.action());
            }

            return showDialogObservable({
                description: i18next.t('unsavedChanges'),
                title: i18next.t('unsavedChangesTitle'),
                okText: i18next.t('discard'),
            }).pipe(
                flatMap(selection => {
                    if (selection === 'OK') {
                        return of(Actions.hide.action());
                    } else {
                        return of(Actions.closeAborted.action());
                    }
                }),
            );
        }),
    );

export default combineEpics(periodUpdated, fetchRecipients, submit, close);
